import { AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@lms/ui';
import { Bilingual } from './Bilingual';
import { copy } from '../copy/copy';
import { useLanguage } from '../lib/LanguageContext';

interface InactivityWarningModalProps {
  isOpen: boolean;
  countdown: number;
  onStayLoggedIn: () => void;
  onLogoutNow: () => void;
}

export function InactivityWarningModal({
  isOpen,
  countdown,
  onStayLoggedIn,
  onLogoutNow,
}: InactivityWarningModalProps) {
  const { t } = useLanguage();

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="inactivity-dialog-title"
      className="fixed inset-0 bg-on-surface/50 backdrop-blur-md z-[9999] flex justify-center items-center p-4 animate-fadeIn"
    >
      <div className="bg-surface-container-lowest rounded-[2rem] w-full max-w-md shadow-level5 overflow-hidden animate-slideUp border border-on-surface/5 p-8 text-center">
        {/* Warning Icon with Pulse Halo */}
        <div className="mx-auto w-16 h-16 rounded-full bg-warning/15 flex items-center justify-center text-warning mb-6 ring-8 ring-warning/5 animate-pulse">
          <AlertTriangle size={32} />
        </div>

        {/* Title */}
        <h3
          id="inactivity-dialog-title"
          className="text-2xl font-headline font-bold text-on-surface tracking-tight mb-2"
        >
          <Bilingual {...copy.inactivity.warningTitle} />
        </h3>

        {/* Description */}
        <p className="text-sm font-body text-on-surface-variant mb-6 leading-relaxed">
          <Bilingual {...copy.inactivity.warningDesc} />
        </p>

        {/* Countdown Badge */}
        <div className="inline-flex items-center gap-2 bg-surface-container-low px-5 py-2.5 rounded-full mb-8 border border-outline-variant/10 shadow-level1">
          <Clock
            size={18}
            className="text-warning animate-spin"
            style={{ animationDuration: '4s' }}
          />
          <span className="font-headline font-bold text-xl text-on-surface tracking-wide">
            {countdown}
          </span>
          <span className="text-xs uppercase font-label font-bold tracking-widest text-on-surface-variant opacity-75">
            {t(copy.inactivity.countdownSuffix)}
          </span>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <Button
            type="button"
            variant="ghost"
            onClick={onLogoutNow}
            className="w-full sm:w-auto text-xs uppercase font-bold tracking-widest text-on-surface-variant hover:text-error"
          >
            <Bilingual {...copy.inactivity.logoutNow} />
          </Button>

          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={onStayLoggedIn}
            className="w-full sm:w-auto px-8 rounded-xl text-xs uppercase font-bold tracking-widest shadow-level2"
          >
            <Bilingual {...copy.inactivity.stayLoggedIn} />
          </Button>
        </div>
      </div>
    </div>
  );
}
